// frontend/src/components/inventaire/HistoriqueCreditModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  CreditCard as CreditIcon,
  Close as CloseIcon,
  TrendingUp as IncreaseIcon,
  TrendingDown as DecreaseIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import api from '../../services/auth';
import { formatCurrency, formatDate, formatPhoneNumber } from '../../../src/components/utils/formatters';

const HistoriqueCreditModal = ({ open, onClose, client, embedded = false, onPayment }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (open && client?.id) {
      fetchHistorique();
    }
  }, [open, client]);

  const fetchHistorique = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/clients/${client.id}/historique_credit/`);
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Erreur chargement historique crédit:', err);
      setError('Impossible de charger l\'historique du crédit');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'achat':
        return <IncreaseIcon fontSize="small" color="error" />;
      case 'paiement':
        return <DecreaseIcon fontSize="small" color="success" />;
      case 'ajustement':
        return <CreditIcon fontSize="small" color="warning" />;
      default:
        return <HistoryIcon fontSize="small" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'achat': return 'error';
      case 'paiement': return 'success';
      case 'ajustement': return 'warning';
      default: return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'achat': return 'Achat';
      case 'paiement': return 'Paiement';
      case 'ajustement': return 'Ajustement';
      default: return type;
    }
  };

  const pourcentageUtilisation = data ? (data.solde_credit / data.limite_credit) * 100 : 0;
  const estEnAlerte = pourcentageUtilisation > 80;
  const estEnDanger = pourcentageUtilisation > 95;

  const content = (
    <Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      ) : data ? (
        <Grid container spacing={3}>
          {/* Résumé du crédit */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditIcon />
                  Situation du crédit
                </Typography>
                
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="textSecondary">
                      Limite de crédit
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(data.limite_credit)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="textSecondary">
                      Solde actuel
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="error.main">
                      {formatCurrency(data.solde_credit)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="textSecondary">
                      Crédit disponible
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {formatCurrency(data.credit_disponible)}
                    </Typography>
                  </Grid>
                </Grid>

                {data.limite_credit > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        Utilisation du crédit
                      </Typography>
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        color={estEnDanger ? 'error.main' : estEnAlerte ? 'warning.main' : 'success.main'}
                      >
                        {pourcentageUtilisation.toFixed(1)}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(pourcentageUtilisation, 100)} 
                      sx={{ 
                        height: 10, 
                        borderRadius: 5,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: estEnDanger ? 'error.main' : estEnAlerte ? 'warning.main' : 'success.main'
                        }
                      }}
                    />
                  </Box>
                )}

                {estEnAlerte && (
                  <Alert 
                    severity={estEnDanger ? "error" : "warning"} 
                    sx={{ mt: 2 }}
                    icon={<WarningIcon />}
                  >
                    {estEnDanger 
                      ? 'Crédit presque épuisé ! Pensez à augmenter la limite ou à récupérer les paiements.'
                      : 'Attention : utilisation du crédit élevée (>80%)'}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Historique des transactions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon />
                Historique des transactions
              </Typography>
              
              {onPayment && data.solde_credit > 0 && (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<PaymentIcon />}
                  onClick={onPayment}
                  size="small"
                >
                  Paiement crédit
                </Button>
              )}
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Montant</TableCell>
                    <TableCell align="right">Solde avant</TableCell>
                    <TableCell align="right">Solde après</TableCell>
                    <TableCell>Motif</TableCell>
                    <TableCell>Utilisateur</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.transactions?.map((transaction) => (
                    <TableRow key={transaction.id} hover>
                      <TableCell>{formatDate(transaction.date_creation)}</TableCell>
                      <TableCell>
                        <Chip
                          icon={getTypeIcon(transaction.type_transaction)}
                          label={getTypeLabel(transaction.type_transaction)}
                          size="small"
                          color={getTypeColor(transaction.type_transaction)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          color={transaction.montant < 0 ? 'success.main' : 'error.main'}
                          fontWeight="bold"
                        >
                          {transaction.montant > 0 ? '+' : ''}
                          {formatCurrency(Math.abs(transaction.montant))}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(transaction.solde_avant)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">
                          {formatCurrency(transaction.solde_apres)}
                        </Typography>
                      </TableCell>
                      <TableCell>{transaction.motif || '-'}</TableCell>
                      <TableCell>{transaction.utilisateur?.username || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {(!data.transactions || data.transactions.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        Aucune transaction de crédit
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      ) : null}
    </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CreditIcon color="primary" />
          <Typography variant="h6">
            Historique crédit - {client?.nom_complet || `${client?.nom} ${client?.prenom || ''}`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {content}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoriqueCreditModal;